import type { FC } from 'react';
import React, { useCallback, useContext, Fragment, useState, useEffect } from 'react';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';

import { PROVIDER_ERROR_CODE } from '@atlaskit/collab-provider';
import type { EditorActions } from '@atlaskit/editor-core';
import { SpotlightManager } from '@atlaskit/onboarding';

import {
	MIGRATE_PAGE_PUBLISH_EXPERIENCE,
	ExperienceTimeout,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { BannerContext, BannerStateContainer } from '@confluence/banners';
import { withFlags } from '@confluence/flags';
import { usePubSubEvent } from '@confluence/pubsub-client';
import { RestrictedPage } from '@confluence/no-permission';
import { useIsTransition } from '@confluence/route-manager';
import {
	EditorFlags,
	NativeCollabErrorFlags,
	ALLOWED_FLAG_MESSAGES,
	NATIVE_COLLAB_ERROR_REASONS,
	getParentContentId,
} from '@confluence/editor-flags';
import { NotFound } from '@confluence/not-found';
import {
	ErrorBoundary,
	isUnauthorizedError,
	isExpectedApolloError,
} from '@confluence/error-boundary';
import { WebPanelLocation } from '@confluence/web-panel-location';
import { ATL_FOOTER, ATL_EDITOR_SAVEBAR, ATL_GENERAL } from '@confluence/web-item-location';
import { useAutoConvertState } from '@confluence/editor-conversion';
import { EditorConversionModal } from '@confluence/editor-conversion/entry-points/EditorConversionModal';
import { isEditPageDraftRestricted } from '@confluence/fabric-providers/entry-points/preloadDraft';
import { CollabRecoveryErrorType, isHeartbeatPermissionError } from '@confluence/native-collab';
import type { CollabEditState, CollabHeartbeatError } from '@confluence/native-collab';
import type { Attribution } from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';
import { PageTitleQuery } from '@confluence/content-title';
import { getApolloClient, markErrorAsHandled } from '@confluence/graphql';

import { useMigratedRecently, useSpaceKeyWatcher } from '../hooks';
import type { DraftSyncResponseError } from '../hooks';

import { MigrationSuccessFlag } from './MigrationSuccessFlag';
import { SameFileNameWarningFlag } from './SameFileNameWarningFlag/SameFileNameWarningFlag';

export type EditorWrapperProps = {
	attribution: Attribution;
	contentId: string;
	contentType: string;
	isUnpublishedDraft: boolean;
	collabEditState: CollabEditState;
	forceDisableEditor: (forceDisable: boolean) => void;
	editorActions?: EditorActions; // used by SameFileNameWarningFlag
	heartbeatError?: CollabHeartbeatError;
	saveDraftError?: DraftSyncResponseError | null;
	stopHeartbeatCallback: () => void;
	children?: React.ReactNode;
	isLivePage?: boolean;
	editorDisabled?: boolean;
};

const HEARTBEAT_ERROR_FLAGS = ['tenant-unavailable', 'unauthorised', 'UNAUTHORIZED'];

const EditorSuccessFlags = withFlags(EditorFlags);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{
	bannerHeight: number;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => (props.bannerHeight > 0 ? `calc(100vh - ${props.bannerHeight}px)` : `100vh`),
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WithPanels = styled.div({
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Panel = styled.div<{
	showPanel?: boolean;
}>({
	margin: 0,
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) => (props?.showPanel ? 'flex' : 'none'),
	flexDirection: 'column',
});

const getFlagData = memoize((flagData) => flagData, isEqual);

export const EditorWrapper: FC<EditorWrapperProps> = ({
	attribution,
	forceDisableEditor,
	collabEditState,
	contentType,
	contentId,
	children,
	isUnpublishedDraft,
	editorActions,
	heartbeatError,
	stopHeartbeatCallback,
	saveDraftError,
	isLivePage,
	editorDisabled,
}) => {
	const { includeTopNav } = useContext(BannerContext);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { collabEditInitialized, collabEditError, collabRecoveryError } = collabEditState;
	const [showCreateFromExtensionFlag, setShowCreateFromExtensionFlag] = useState<{
		showFlag: boolean;
		flagData: any;
	}>({ showFlag: false, flagData: undefined });
	const [showRestoreSuccessFlag, setShowRestoreSuccessFlag] = useState<any>(false);
	const [blogifyConversionSuccess, setBlogifyConversionSuccess] = useState<{
		showFlag: boolean;
		flagData: any;
	}>({ showFlag: false, flagData: undefined });
	const [heartbeatErrorFlag, setHeartbeatErrorFlag] = useState<{
		showFlag: boolean;
		heartbeatError: CollabHeartbeatError | undefined;
	}>({ showFlag: false, heartbeatError: undefined });
	const [isAutoConverted] = useAutoConvertState();
	const { userId } = useSessionData();
	const isTransition = useIsTransition();

	// Analytics
	const migratedRecently = useMigratedRecently(contentId);

	// track publish experience in page migration
	// copied from ContentEditorBindingsComponent
	if (migratedRecently) {
		experienceTracker.start({
			name: MIGRATE_PAGE_PUBLISH_EXPERIENCE,
			id: contentId,
			timeout: ExperienceTimeout.EDITOR_PUBLISH,
		});
	}

	// Show a draft create success flag if this draft was created from the create page extension
	useEffect(() => {
		// Ensure editor is ready
		if (collabEditInitialized && !editorDisabled) {
			const parentContentId = getParentContentId();
			if (parentContentId) {
				// Get the parent page title & show a draft create success flag
				let flagTimeout;
				getApolloClient()
					.query({
						query: PageTitleQuery,
						variables: { contentId: parentContentId },
					})
					.then((result) => {
						const parentTitle = result.data?.singleContent?.title;
						flagTimeout = setTimeout(() => {
							setShowCreateFromExtensionFlag({
								showFlag: true,
								flagData: {
									parentTitle,
									parentContentId,
								},
							});
						}, 1000);
					})
					.catch((e) => {
						if (isUnauthorizedError(e) || isExpectedApolloError(e)) {
							markErrorAsHandled(e);
						}
					});

				return () => {
					clearTimeout(flagTimeout);
				};
			}
		}
	}, [collabEditInitialized, editorDisabled]);

	const showBlogifyConversionSuccessFlag = blogifyConversionSuccess.showFlag;
	const blogifyConversionSuccessFlagData = blogifyConversionSuccess.flagData;

	const handleRestore = useCallback(
		(_, eventData: any) => {
			const lastPublisher = eventData?.accountId;
			// Show this flag only if the same user triggered the restore
			// Otherwise, useUpdateEvent handles when a different user restores
			return !showRestoreSuccessFlag && userId === lastPublisher && setShowRestoreSuccessFlag(true);
		},
		[showRestoreSuccessFlag, userId],
	);

	const handleBlogify = useCallback(
		(_aris: any, pubsubRequestBody: any) => {
			// force disable NCS editor whenever page is converted to blogpost
			forceDisableEditor && forceDisableEditor(true);

			const requestBody = pubsubRequestBody?.blogifyRequestBody;
			!showBlogifyConversionSuccessFlag &&
				requestBody &&
				setBlogifyConversionSuccess({ showFlag: true, flagData: requestBody });
		},
		[forceDisableEditor, showBlogifyConversionSuccessFlag],
	);

	useEffect(() => {
		if (heartbeatError) {
			if (heartbeatError.reason === 'tenant-unavailable') {
				forceDisableEditor(true);
			} else if (isHeartbeatPermissionError(heartbeatError)) {
				stopHeartbeatCallback();
			}

			if (heartbeatErrorFlag.heartbeatError?.reason !== heartbeatError.reason) {
				// Only update error flag data if error is different
				setHeartbeatErrorFlag({
					showFlag: HEARTBEAT_ERROR_FLAGS.includes(heartbeatError.reason),
					heartbeatError,
				});
			}
		}
		// exclude heartbeatErrorFlag.heartbeatError?.reason from dep list to prevent firing twice in a row
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [heartbeatError, forceDisableEditor, stopHeartbeatCallback]);

	// PubSub
	usePubSubEvent({
		contentId,
		contentType,
		eventName: `restore:${contentType}`,
		onEvent: handleRestore,
	});

	// https://hello.atlassian.net/wiki/spaces/~894124079/pages/1424063128/CEMS-2279+Add+Pubsub+Event+When+Page+Converted+To+Blogpost
	usePubSubEvent({
		contentId,
		contentType,
		eventName: `blogified:blogpost`,
		onEvent: handleBlogify,
		skip: contentType === 'blogpost', // only listen for blogify events on pages
	});

	// Update the URL of the draft with new space key
	// 1. when another users move the page to different space
	// 2. when user returns to a bookmarked page which has since been moved
	useSpaceKeyWatcher({ contentType });

	if (collabRecoveryError) {
		// Collab Reset mutation can return 403 or 404 directly from Confluence upon editor loading (i.e. perms/page deleted hasn't changed during editing session, so no flag)
		if (
			collabRecoveryError instanceof CollabRecoveryErrorType &&
			collabRecoveryError.status === 403
		) {
			return <RestrictedPage accessType="edit" contentId={contentId} />;
		} else if (
			collabRecoveryError instanceof CollabRecoveryErrorType &&
			collabRecoveryError.status === 404
		) {
			return <NotFound />;
		}
	}

	const isResourceDeletedOrArchived =
		collabEditError &&
		'reason' in collabEditError &&
		collabEditError.reason === NATIVE_COLLAB_ERROR_REASONS.RESOURCE_DELETED;

	if (collabEditError?.status === 403) {
		// The collab service has emitted an error.
		// Cases for the 403:
		//  (1) on initial editor load when the collab provider is not initialized
		//  (2) user's permissions are revoked during the editing session.
		//  (3) page was deleted or archived: if this is the case, reason will be NATIVE_COLLAB_ERROR_REASONS.RESOURCE_DELETED
		// Most of the time when a 403 occurs, we want to kick the user out of the editor, and in the second case we want to show an error flag explaining that permissions have changed.
		// If we're on a live page and the page was deleted or archived, we don't want to show <NotFound /> nor kick the user out. Instead we expect the editor to be disabled and we'll show a flag. (This also helps resolve a race condition where <NotFound /> can be shown before the archiving user gets redirected.)
		if (!isLivePage || (isLivePage && !isResourceDeletedOrArchived)) {
			return (
				<Fragment>
					{collabEditInitialized && <NativeCollabErrorFlags collabEditError={collabEditError} />}
					{isResourceDeletedOrArchived ? (
						<NotFound />
					) : (
						<RestrictedPage accessType="edit" contentId={contentId} />
					)}
				</Fragment>
			);
		} else if (isLivePage && isResourceDeletedOrArchived) {
			// Stop the heartbeat callback to prevent it from kicking the user out of the editor. We want the user to stay in the editor with a flag visible, until they refresh (or if they're the archiver, are redirected).
			stopHeartbeatCallback();
		}
	}

	if (!isTransition && isEditPageDraftRestricted()) {
		return <RestrictedPage accessType="edit" contentId={contentId} />;
	}

	if (isHeartbeatPermissionError(heartbeatError)) {
		return (
			<Fragment>
				<NativeCollabErrorFlags heartbeatError={heartbeatError} />
				<RestrictedPage accessType="edit" contentId={contentId} />
			</Fragment>
		);
	}

	if (saveDraftError) {
		forceDisableEditor(true);
		// For other errors, we still want to render editor content, but in disabled state
		if (saveDraftError.statusCode === 403 || saveDraftError.statusCode === 404) {
			return (
				<Fragment>
					<NativeCollabErrorFlags saveDraftError={saveDraftError} />
					{saveDraftError.statusCode === 403 && (
						<RestrictedPage accessType="edit" contentId={contentId} />
					)}
					{saveDraftError.statusCode === 404 && <NotFound />}
				</Fragment>
			);
		}
	}

	return (
		// TODO: display ADFParseError if passed in as a prop (from content reconciliation)
		<ErrorBoundary modal attribution={attribution}>
			<SpotlightManager>
				<Subscribe to={[BannerStateContainer]}>
					{(bannerState: BannerStateContainer) => (
						// The wiki-content class name is required to avoid breaking macros that rely on DOM selectors
						<Wrapper
							bannerHeight={bannerState.getTotalHeight(includeTopNav)}
							data-testid="editor-wrapper"
							data-fabric-mode={isUnpublishedDraft ? 'create' : 'edit'}
							data-content-subtype={isLivePage ? 'live' : undefined}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className="wiki-content"
						>
							<WithPanels>
								<Panel>
									<WebPanelLocation location={ATL_GENERAL} fetchPolicy="cache-first" />
								</Panel>
								{children}
								{!isLivePage && (
									<Panel>
										<WebPanelLocation location={ATL_EDITOR_SAVEBAR} />
									</Panel>
								)}
							</WithPanels>
							{/* We do this because we want to hide the panel's UI in live pages but keep the content (vendors often use background scripts for this footer extension point) */}
							<Panel showPanel={!isLivePage} data-testid="atl-footer-wrapper">
								<WebPanelLocation location={ATL_FOOTER} />
							</Panel>
							{heartbeatErrorFlag.showFlag && (
								<NativeCollabErrorFlags heartbeatError={heartbeatErrorFlag.heartbeatError} />
							)}
							{collabEditError &&
								(collabEditError.status === 500 ||
									collabEditError.code === PROVIDER_ERROR_CODE.LOCKED) && ( // unhandled error, no recovery steps at this point
									<NativeCollabErrorFlags collabEditError={collabEditError} />
								)}
							{collabRecoveryError &&
								collabRecoveryError instanceof CollabRecoveryErrorType &&
								collabRecoveryError.status === 500 && (
									<NativeCollabErrorFlags collabRecoveryError={collabRecoveryError} />
								)}
							{isLivePage &&
								collabEditError?.status === 403 &&
								isResourceDeletedOrArchived && ( // If page is archived or deleted while on a live page, show a flag
									<NativeCollabErrorFlags collabEditError={collabEditError} />
								)}
							{showRestoreSuccessFlag && (
								<EditorSuccessFlags
									flagData={getFlagData({
										[ALLOWED_FLAG_MESSAGES.RESTORE_CONTENT_SUCCESS]: true,
									})}
									contentId={contentId}
									contentType={contentType}
									isUnpublishedDraft={isUnpublishedDraft}
								/>
							)}
							{showBlogifyConversionSuccessFlag && (
								<EditorSuccessFlags
									flagData={getFlagData({
										[ALLOWED_FLAG_MESSAGES.BLOGIFY_BLOGPOST_SUCCESS]: true,
										...blogifyConversionSuccessFlagData,
									})}
									contentId={contentId}
									contentType={contentType}
									isUnpublishedDraft={isUnpublishedDraft}
								/>
							)}
							{showCreateFromExtensionFlag.showFlag && (
								<EditorSuccessFlags
									flagData={getFlagData({
										[ALLOWED_FLAG_MESSAGES.CREATED_FROM_EXTENSION]: true,
										...showCreateFromExtensionFlag.flagData,
									})}
								/>
							)}
							<MigrationSuccessFlag contentId={contentId} />
							{editorActions ? (
								<SameFileNameWarningFlag editorActions={editorActions} contentId={contentId} />
							) : null}
							{isAutoConverted ? <EditorConversionModal /> : null}
							{saveDraftError && <NativeCollabErrorFlags saveDraftError={saveDraftError} />}
						</Wrapper>
					)}
				</Subscribe>
			</SpotlightManager>
		</ErrorBoundary>
	);
};
